import React, { ReactNode, createContext, useCallback, useMemo, useState } from 'react'

/**
 * Channel between the preview panel (which proves conflicts) and the condition
 * cards (which show amber outlines). A sibling of HighlightedPrereqContext —
 * the established pattern for decorating cards from outside PatternTreeContext.
 */
export const PreviewConflictContext = createContext<{
  conflictConditionIds: string[]
  setConflictConditionIds: (ids: string[]) => void
}>({
  conflictConditionIds: [],
  setConflictConditionIds: () => undefined,
})

const PreviewConflictProvider = ({ children }: { children: ReactNode }) => {
  const [conflictConditionIds, setIds] = useState<string[]>([])
  // Every solve produces a fresh array; bail on shallow equality so the
  // steady-state no-conflict case ([] → []) never re-renders the whole
  // editor subtree this provider wraps.
  const setConflictConditionIds = useCallback((ids: string[]) => {
    setIds((previous) =>
      previous.length === ids.length && previous.every((id, i) => id === ids[i]) ? previous : ids
    )
  }, [])
  const value = useMemo(
    () => ({ conflictConditionIds, setConflictConditionIds }),
    [conflictConditionIds, setConflictConditionIds]
  )
  return <PreviewConflictContext.Provider value={value}>{children}</PreviewConflictContext.Provider>
}

export default PreviewConflictProvider
